import { FC, useState } from "react";
import { WizardProps } from "./types";
import { ProgressWizard } from "./ProgressWizard";
import { BuildingInformationForm } from "../steps/1-building-info/BuildingInformation";
import { BuildingLayoutForm } from "../steps/2-building-layout/BuildingLayout";
import { emptyBuildingConfiguration } from "../../../../_website/building-configurations/empty";
import { BuildingComponentsAndWindowComponents } from "../steps/3-building-components/BuildingComponentsAndWindowComponents";
import { AssignBuildToAssignments } from "../steps/4-associate-build-to-assignments/AssignBuildToAssignments";
import { StudentFormSubmission } from "../steps/4-associate-build-to-assignments/StudentFormSubmission";
import { WizardState } from "../wizard.types";
import { ReviewConfiguration } from "../steps/5-review-config/ReviewConfiguration";
import { application } from "../../../../_runtime/bootstrap";
import { clone } from "ramda";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { useNotifications } from "@hooks";
import { IUserNotificationService } from "@dirootie-web/components-types";
import { useUserContext } from "../../../../_runtime/iam/session.context";
import { useLoading } from "../../../../components-common/_shared/loading/use-loading.hook";
import { Loader } from "taltech-styleguide";

/**
 * Maps data to dropdown options.
 *
 * @param {Array} data - The data to map.
 * @param {string} valueField - The field to use for the option value.
 * @param {string} textField - The field to use for the option text.
 * @returns {Array} - The mapped dropdown options.
 */
export const mapDataToDropdownOptions = (
  data: any,
  valueField: string,
  textField: string
) => {
  return data.map((x: any) => ({
    value: x[valueField],
    text: x[textField],
  }));
};

/**
 * Building Information Step Configuration
 *
 * Defines the configuration for the Building Information step.
 */
const buildingInformationStep = {
  title: "Building information",
  titleShort: "Information",
  component: {
    Component: BuildingInformationForm,
    props: {
      element: {
        ...emptyBuildingConfiguration,
        buildingParameters: mapDataToDropdownOptions([], "id", "name"),
      },
      cat: "form",
    },
  },
};

/**
 * Building Layout Step Configuration
 *
 * Defines the configuration for the Building Layout step.
 */
const buildingLayoutStep = {
  title: "Building layout",
  titleShort: "Layout",
  component: {
    Component: BuildingLayoutForm,
    props: {
      element: {
        ...emptyBuildingConfiguration,
      },

      cat: "form",
    },
  },
};

/**
 * Building Components Step Configuration
 *
 * Defines the configuration for the Building Components step.
 */
const buildingComponentsStep = {
  title: "Building components",
  titleShort: "Components",
  component: {
    Component: BuildingComponentsAndWindowComponents,
    props: {
      element: {
        ...emptyBuildingConfiguration,
      },
      dropdownOptions: {
        wallInterior: [],
        wallExterior: [],
        floor: [],
        floorGround: [],
        roof: [],
        windowType: [],
      },
    },
    cat: "form",
  },
};

/**
 * Lecturer Finish Step Configuration
 *
 * Defines the configuration for the Lecturer Finish step.
 */
const lecturerFinishStep = {
  titleShort: "Configuration",
  title: "Configuration Information",
  component: {
    Component: AssignBuildToAssignments,
    props: {
      element: {
        ...emptyBuildingConfiguration,
      },
    },
    cat: "form",
  },
};

/**
 * Student Finish Step Configuration
 *
 * Defines the configuration for the Student Finish step.
 */
const studentFinishStep = {
  titleShort: "Configuration",
  title: "Configuration Information",
  component: {
    Component: StudentFormSubmission,
    props: {
      element: {
        ...emptyBuildingConfiguration,
      },
    },

    cat: "form",
  },
};

/**
 * Review Configuration Step Configuration
 *
 * Defines the configuration for the Review Configuration step.
 */
const reviewConfigStep = {
  titleShort: "Review",
  title: "Review Configuration",
  component: {
    Component: ReviewConfiguration,
    props: {
      element: {},
    },

    cat: "form",
  },
};

/**
 * Maps element types values.
 *
 * Clones and modifies element types, converting string options to arrays.
 *
 * @param {Object} elementTypes - The element types to map.
 * @returns {Object} - The mapped element types.
 */
const mapElementTypesValues = (elementTypes: any) => {
  const newElementTypes = clone(elementTypes);
  const properties = [
    "wallInterior",
    "wallExterior",
    "floor",
    "floorGround",
    "roof",
    "windowType",
  ];

  properties.forEach((property) => {
    if (typeof newElementTypes[property].options.elements === "string") {
      newElementTypes[property].generatorType = "LIST";
      newElementTypes[property].options.elements = [
        elementTypes[property].options.elements,
      ];
    }
  });

  return newElementTypes;
};

/**
 * Wizard Properties
 *
 * Defines the properties and submission logic for the wizard.
 *
 * @param {NavigateFunction} nav - The navigation function.
 * @param {IUserNotificationService} userNotifications - The user notification service.
 * @param {boolean} [isTeacher=true] - Flag indicating if the user is a teacher.
 * @param {string} [userId] - The user ID.
 * @returns {Object} - The wizard properties.
 */
const wizardProps = (
  nav: NavigateFunction,
  isTeacher: boolean = true,
  userId: string | undefined
) => {
  return {
    stepIndex: 1,
    steps: [
      buildingInformationStep,
      buildingLayoutStep,
      buildingComponentsStep,
      isTeacher ? lecturerFinishStep : studentFinishStep,
      reviewConfigStep,
    ],
    onSubmit: async (state: any) => {
      const step1 = state.steps[0].data;
      const step2 = state.steps[1].data;
      const step3 = mapElementTypesValues(state.steps[2].data);
      const step4 = state.steps[3].data;

      const mappedData = {
        name: step4.name,
        description: step4.description,
        subjectId: isTeacher ? step4.assignmentId : userId,
        configuration: {
          info: {
            name: step1.name,
            description: step1.description,
            location: step1.location,
            rotation: step1.rotation,
          },
          height: step1.height,
          numberOfLevels: step1.numberOfLevels,
          lengthOptions: {
            modular: step1.modular,
            moduleLength: step1.moduleSize,
            lengthUnit: 0,
          },
          buildingParameters: step1.buildingParameters,
          layout: {
            rowFirst: {
              numberOfRooms: step2.rowFirst.numberOfRooms,
              width: step2.rowFirst.width,
              rooms: step2.rowFirst.rooms,
            },
            corridor: step2.corridor,
            rowSecond: {
              numberOfRooms: step2.rowSecond.numberOfRooms,
              width: step2.rowSecond.width,
              rooms: step2.rowSecond.rooms,
            },
          },
          elementTypes: {
            wallInterior: step3.wallInterior,
            wallExterior: step3.wallExterior,
            floor: step3.floor,
            floorGround: step3.floorGround,
            roof: step3.roof,
            windowType: step3.windowType,
          },
        },
        url: "",
        sourceType: step4.courseId ? "COURSE" : "USER",
        // if a user is a teacher, then the sourceId should be the courseId because this configuration is associated with a course
        // but if a user is a student, then the sourceId should be the userId because this configuration is associated with the user
        sourceId: isTeacher ? step4.courseId : userId,
      };

      return await application
        .getHttpClient()
        .post("buildingsconfigms/configurations", mappedData)
        .then((res) => {
          nav(-1);
          return res;
        })
        .catch((e) => e);
    },
  } as any;
};

/**
 * ConfigurationForm Component
 *
 * Renders the configuration form wizard.
 *
 * @param {WizardProps} props - The wizard properties.
 * @returns {JSX.Element} - The rendered configuration form.
 */
export const ConfigurationForm: FC<WizardProps> = ({ ...props }) => {
  const nav = useNavigate();
  const { isInCourseAssignment = false } = props;
  const { role, userId } = useUserContext();
  const body = (
    <ProgressWizard
      isInCourseAssignment={isInCourseAssignment}
      {...wizardProps(nav, role !== "STUDENT", userId)}
      title="Building configuration"
    />
  );
  return body;
};
